import { useMemo } from "react";
import { Option } from "@/constants/common";
import { TENANT_FEATURE } from "@/constants/tenant-feature-constant";
import {
  DEFAULT_FORM_LIST,
  DEFAULT_VISIT_NOTE_FORM_TYPE,
  DefaultForm,
  PREMIER_MD_DEFAULT_VISIT_NOTE_FORM_TYPE,
} from "@/constants/visit-note-form-constant";
import { EncounterSumaryFormTypeEnum } from "@/services";
import { useTenantFeatures } from "./use-tenant-features";

export interface UseVisitNoteFormOptionsResult {
  forms: DefaultForm[];
  options: Option[];
  defaultFormType: EncounterSumaryFormTypeEnum;
  defaultOption: Option | undefined;
  /** The given note type when this tenant may use it, otherwise the tenant default. */
  resolveFormType: (formType?: string | null) => EncounterSumaryFormTypeEnum;
}

/** Visit-note types the current tenant may create: Premier MD realms get only their own. */
export const useVisitNoteFormOptions = (): UseVisitNoteFormOptionsResult => {
  const { pickExclusive, isEnabled } = useTenantFeatures();

  return useMemo(() => {
    const forms = pickExclusive(
      DEFAULT_FORM_LIST,
      TENANT_FEATURE.PREMIER_MD_F2F_NOTES
    );

    const options = forms.map(({ value, name }) => ({ value, label: name }));
    const defaultFormType = isEnabled(TENANT_FEATURE.PREMIER_MD_F2F_NOTES)
      ? PREMIER_MD_DEFAULT_VISIT_NOTE_FORM_TYPE
      : DEFAULT_VISIT_NOTE_FORM_TYPE;

    return {
      forms,
      options,
      defaultFormType,
      defaultOption: options.find(({ value }) => value === defaultFormType),
      resolveFormType: (formType) =>
        forms.find(({ value }) => value === formType)?.value ?? defaultFormType,
    };
  }, [pickExclusive, isEnabled]);
};
